/**
 * `StatusPill` - shared account-status pill primitive.
 *
 * Mirrored from `features/accounts/columns.tsx` (which historically held
 * an in-file copy) so both the list table and the detail page render the
 * same accessible name and colour signal. The component index in `doc/design-system.md`
 * Section 3 explicitly calls this out as reused across surfaces.
 *
 * Colour is paired with text + icon-equivalent shape, per the MUR "Design Tokens and Accessibility" page
 * "Colour is not the only signal".
 */
import type { AccountStatus } from '../lib/bff-client'

export interface StatusPillProps {
  readonly status: AccountStatus
}

export function StatusPill({ status }: StatusPillProps): JSX.Element {
  const { fg, bg } = pillColours(status)
  return (
    <span
      aria-label={`Status ${status}`}
      className={`inline-flex items-center rounded-sm px-murmuration-2 py-[2px] text-murmuration-label font-medium ${fg} ${bg}`}
    >
      {status}
    </span>
  )
}

function pillColours(status: AccountStatus): { fg: string; bg: string } {
  switch (status) {
    case 'active':
      return { fg: 'text-status-success', bg: 'bg-status-success-bg' }
    case 'suspended':
      return { fg: 'text-status-warning', bg: 'bg-status-warning-bg' }
    case 'closed':
      return { fg: 'text-status-neutral', bg: 'bg-status-neutral-bg' }
    case 'deleted':
      return { fg: 'text-status-danger', bg: 'bg-status-danger-bg' }
  }
}
